/**
 * AsrReviewEntry — a compact, neutral card for an Additional Services Request. It identifies the
 * request (no paraphrase of the binding terms) and opens the full-screen SigningModal, where the
 * actual document is reviewed and signed with the signature placed on the real acceptance line.
 * Signing fires onRespond('approve', { signerName, signatureDataUrl, method, font, consent }).
 */
import { useState } from 'react'
import SigningModal from '../SigningModal.jsx'

function fmtDate(iso) {
  if (!iso) return null
  const d = new Date(iso + (iso.includes('T') ? '' : 'T00:00:00'))
  return isNaN(d) ? null : d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
}

export default function AsrReviewEntry({ entry, response, onRespond }) {
  const d = entry.data || {}
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const signed = response && response.kind === 'approve'

  async function submit(payload) {
    if (busy) return
    setBusy(true)
    try { await onRespond('approve', payload); setOpen(false) }
    finally { setBusy(false) }
  }

  if (signed) {
    const pl = response.payload || {}
    return (
      <div style={S.wrap}>
        <div style={S.eyebrow}>Additional Services</div>
        <div style={S.title}>Approved — thank you.</div>
        <p style={S.body}>This additional services request has been approved{pl.signerName ? ` and signed by ${pl.signerName}` : ''}. Our team will proceed accordingly.</p>
        {pl.signedPdfUrl
          ? <a style={S.docLink} href={pl.signedPdfUrl} target="_blank" rel="noopener">View signed copy ↗</a>
          : d.pdfUrl && <a style={S.docLink} href={d.pdfUrl} target="_blank" rel="noopener">View the request ↗</a>}
      </div>
    )
  }

  return (
    <div style={S.wrap}>
      <div style={S.eyebrow}>Additional Services Request{d.number ? ` · No. ${d.number}` : ''}</div>
      <div style={S.title}>{d.title || 'Additional services for your project'}</div>
      {fmtDate(d.issueDate) && <div style={S.date}>Issued {fmtDate(d.issueDate)}</div>}
      <p style={S.body}>Please review the full request and sign it below. The complete scope, fee, and any stipulations are set out in the document itself.</p>
      <button style={S.cta} onClick={() => setOpen(true)} disabled={!d.pdfUrl}>Review &amp; sign</button>
      {!d.pdfUrl && <div style={S.unavail}>The document is being prepared. Please check back shortly.</div>}

      {open && d.pdfUrl && (
        <SigningModal
          pdfUrl={d.pdfUrl}
          title={d.title || `Additional Services Request${d.number ? ' No. ' + d.number : ''}`}
          subtitle="Additional Services Request"
          anchorQueries={['Accepted by (signature)', 'Accepted by']}
          consentLabel="I agree to sign this additional services request electronically, and that my electronic signature is the legal equivalent of my handwritten signature and constitutes my acceptance of these terms."
          busy={busy}
          onClose={() => setOpen(false)}
          onSubmit={submit}
        />
      )}
    </div>
  )
}

const S = {
  wrap: { fontFamily: '"DM Sans", sans-serif', color: '#07272D' },
  eyebrow: { fontFamily: '"Montserrat", sans-serif', fontSize: 10, fontWeight: 600, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#7a9aaa', marginBottom: 10 },
  title: { fontFamily: '"Montserrat", sans-serif', fontSize: 22, fontWeight: 300, color: '#07272D', marginBottom: 12 },
  body: { fontSize: 14, lineHeight: 1.6, color: '#3a5260', marginBottom: 18 },
  date: { fontFamily: '"DM Mono", monospace', fontSize: 12, color: '#7a9aaa', marginBottom: 14 },
  docLink: { display: 'inline-block', fontSize: 13, color: '#1a6fa8', textDecoration: 'none', marginBottom: 18 },
  cta: { fontFamily: '"DM Sans", sans-serif', fontSize: 13, fontWeight: 600, color: '#fff', background: '#07272D', border: 'none', borderRadius: 6, padding: '11px 22px', cursor: 'pointer' },
  unavail: { fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: 'rgba(7,39,45,0.5)', marginTop: 10 },
}
